import { useEffect, useRef, type ClipboardEvent, type MouseEvent } from 'react';
import { Bold, Eraser, Italic, Link2, List, ListOrdered, Underline } from 'lucide-react';
import { plainTextToEditorHtml, safeHref } from '../../lib/mailEditorHtml';

type EditorCommand = 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'insertOrderedList';

/** Runs an editing command when the browser supports it (jsdom and some embeds do not). */
function exec(command: string, value?: string): boolean {
  if (typeof document.execCommand !== 'function') return false;
  return document.execCommand(command, false, value);
}

/** Inserts plain text at the caret using the Selection/Range API (fallback when execCommand is unavailable). */
function insertPlainTextAtCaret(root: HTMLElement, text: string) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !root.contains(sel.anchorNode)) {
    root.appendChild(document.createTextNode(text));
    return;
  }
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const frag = document.createDocumentFragment();
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  lines.forEach((line, i) => {
    if (i > 0) frag.appendChild(document.createElement('br'));
    if (line) frag.appendChild(document.createTextNode(line));
  });
  const last = frag.lastChild;
  range.insertNode(frag);
  if (last) {
    range.setStartAfter(last);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

const TOOLS: { command: EditorCommand; label: string; icon: typeof Bold }[] = [
  { command: 'bold', label: 'Bold', icon: Bold },
  { command: 'italic', label: 'Italic', icon: Italic },
  { command: 'underline', label: 'Underline', icon: Underline },
  { command: 'insertUnorderedList', label: 'Bulleted list', icon: List },
  { command: 'insertOrderedList', label: 'Numbered list', icon: ListOrdered },
];

/**
 * Minimal rich-text body editor (contentEditable + execCommand, no dependencies). It is uncontrolled:
 * `initialText` seeds it once and `onInput` reports the raw innerHTML, which the caller must pass through
 * sanitizeEditorHtml before it is sent anywhere. Pasting always inserts plain text.
 */
export function MailEditor({
  initialText,
  disabled,
  onInput,
  autoFocus,
}: {
  initialText: string;
  disabled?: boolean;
  onInput: (rawHtml: string, userEdit: boolean) => void;
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = plainTextToEditorHtml(initialText); // text is escaped by plainTextToEditorHtml
    onInput(el.innerHTML, false);
    if (autoFocus) {
      el.focus();
      // Caret at the very top, above the quoted original.
      const sel = window.getSelection();
      if (sel && el.firstChild) {
        const range = document.createRange();
        range.setStart(el.firstChild, 0);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
    // Seed once per mount; the parent re-mounts the editor for a new draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const report = () => ref.current && onInput(ref.current.innerHTML, true);

  // Keep the selection inside the editor when a toolbar button is pressed.
  const keepSelection = (e: MouseEvent) => e.preventDefault();

  const run = (command: string, value?: string) => {
    ref.current?.focus();
    exec(command, value);
    report();
  };

  const addLink = () => {
    const input = window.prompt('Link address (https:// or mailto:)');
    if (input === null) return;
    const href = safeHref(input.trim());
    if (!href) {
      window.alert('Only http(s) and mailto links are allowed.');
      return;
    }
    run('createLink', href);
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (!text || !ref.current) return;
    if (!exec('insertText', text)) insertPlainTextAtCaret(ref.current, text);
    report();
  };

  return (
    <div className="mail-editor">
      <div className="mail-editor-toolbar" role="toolbar" aria-label="Formatting">
        {TOOLS.map(({ command, label, icon: Icon }) => (
          <button
            key={command}
            type="button"
            className="btn btn-xs"
            title={label}
            aria-label={label}
            disabled={disabled}
            onMouseDown={keepSelection}
            onClick={() => run(command)}
          >
            <Icon size={14} aria-hidden />
          </button>
        ))}
        <button
          type="button"
          className="btn btn-xs"
          title="Insert link"
          aria-label="Insert link"
          disabled={disabled}
          onMouseDown={keepSelection}
          onClick={addLink}
        >
          <Link2 size={14} aria-hidden />
        </button>
        <button
          type="button"
          className="btn btn-xs"
          title="Clear formatting"
          aria-label="Clear formatting"
          disabled={disabled}
          onMouseDown={keepSelection}
          onClick={() => {
            ref.current?.focus();
            exec('removeFormat');
            exec('unlink');
            report();
          }}
        >
          <Eraser size={14} aria-hidden />
        </button>
      </div>
      <div
        ref={ref}
        className="mail-editor-body"
        role="textbox"
        aria-multiline="true"
        aria-label="Message body"
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck
        onInput={report}
        onPaste={onPaste}
        onDrop={(e) => e.preventDefault()}
      />
    </div>
  );
}
